import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { Search, X, User, Pencil } from "lucide-react";

/**
 * Server-side typeahead for picking a Customer — scales to thousands of
 * records (a plain <select> rendering every customer becomes unusable past a
 * few hundred rows). Debounced search over `GET /customers-search?q=...`.
 * Shows the picked customer as a chip; click "Change" to search again.
 *
 * Props:
 *   selected: customer object ({ id, customer_name, phone, email, ... }) or null
 *   onSelect: (customer) => void
 *   placeholder, testid, required
 */
export function CustomerPicker({ selected, onSelect, placeholder = "Search by name, phone or email…", testid = "customer-picker", required = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  // Guards against a stray focus/click landing on the just-collapsed picker's
  // new position (e.g. the "Change" chip button rendering exactly where the
  // clicked result row used to be) within the same interaction tick.
  const justSelectedRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/customers-search?q=${encodeURIComponent(q)}&limit=20`);
        if (alive) setResults(data || []);
      } catch {
        if (alive) setResults([]);
      } finally {
        if (alive) setSearching(false);
      }
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q, open]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (selected && !open) {
    return (
      <div className="border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center justify-between" data-testid={`${testid}-selected`}>
        <div className="flex items-center gap-2 text-[12.5px] min-w-0">
          <User className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" />
          <span className="font-medium text-[#0F1A12] truncate">{selected.customer_name}</span>
          <span className="text-[#475569] font-mono text-[11px] flex-shrink-0">· {selected.phone}</span>
        </div>
        <button
          type="button"
          onClick={() => { if (justSelectedRef.current) return; setQ(""); setOpen(true); }}
          data-testid={`${testid}-change`}
          className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] hover:underline flex-shrink-0 ml-2"
        >
          <Pencil className="w-3 h-3" /> Change
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 px-2.5 py-2 border border-[#DDE5D8] bg-white focus-within:border-[#1F4E2C]" data-testid={`${testid}-input-row`}>
        <Search className="w-3.5 h-3.5 text-[#94A3B8] flex-shrink-0" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (justSelectedRef.current) return; setOpen(true); }}
          placeholder={placeholder}
          required={required && !selected}
          data-testid={`${testid}-search-input`}
          className="flex-1 text-[12.5px] outline-none min-w-0 bg-transparent"
        />
        {selected && (
          <button type="button" onClick={() => setOpen(false)} className="text-[#475569] hover:text-[#0F1A12] flex-shrink-0" data-testid={`${testid}-close`}>
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="border border-t-0 border-[#DDE5D8] bg-white max-h-56 overflow-y-auto shadow-sm relative z-10" data-testid={`${testid}-results`}>
          {searching && <div className="text-[11px] text-[#475569] font-mono px-3 py-2">Searching…</div>}
          {!searching && results.length === 0 && (
            <div className="text-[11px] text-[#94A3B8] font-mono px-3 py-2">
              {q.trim() ? "No matching customers" : "No customers yet"}
            </div>
          )}
          {!searching && results.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                justSelectedRef.current = true;
                onSelect(c);
                setOpen(false);
                setQ("");
                setTimeout(() => { justSelectedRef.current = false; }, 400);
              }}
              data-testid={`${testid}-row-${c.id}`}
              className="w-full text-left px-3 py-2 border-b border-[#F4F6F1] hover:bg-[#F4F6F1] flex items-center gap-2 text-[12px]"
            >
              <span className="flex-1 truncate text-[#0F1A12] font-medium">{c.customer_name}</span>
              <span className="text-[10px] text-[#475569] font-mono truncate flex-shrink-0">{c.phone}</span>
              <span className="text-[9px] text-[#94A3B8] font-mono uppercase tracking-wider flex-shrink-0">{c.customer_type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
